import { useEffect, useMemo, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { ArrowLeft, PackageOpen, Check } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { zl } from "@/lib/shop";
import { shopApi, useShopAuth } from "@/lib/shopAuth";
import { addSizes } from "@/lib/b2bCart";
import RedfoxLayout from "./RedfoxLayout";
import { Button } from "@/components/ui/button";

const StepHeader = ({ n, label, done }) => (
  <div className="mb-2 flex items-center gap-2">
    <span className={`grid h-6 w-6 place-items-center rounded-full text-xs font-bold ${done ? "bg-red-600 text-white" : "bg-white/10 text-neutral-300"}`}>{done ? <Check className="h-3.5 w-3.5" /> : n}</span>
    <p className="text-xs font-bold uppercase tracking-wider text-neutral-400">{label}</p>
  </div>
);

export default function MyStoreProduct() {
  const { pid } = useParams();
  const auth = useShopAuth();
  const navigate = useNavigate();
  const [p, setP] = useState(null);
  const [store, setStore] = useState(null);
  const [err, setErr] = useState("");
  const [color, setColor] = useState("");
  const [variant, setVariant] = useState(null);
  const [mainPhoto, setMainPhoto] = useState("");
  const [qty, setQty] = useState({});

  useEffect(() => {
    if (!auth?.ready || !auth.customer) return;
    shopApi.get(`/b2b/my/products/${pid}`).then((r) => {
      const prod = r.data;
      setP(prod);
      const colors = prod.colors || [];
      const variants = prod.variants || [];
      const c0 = colors.length === 1 ? colors[0] : "";
      const v0 = variants.length === 1 ? variants[0] : null;
      setColor(c0);
      setVariant(v0);
      setMainPhoto(v0?.photo || (c0 && (prod.color_photos || {})[c0]) || (prod.photos || [])[0] || "");
      shopApi.get("/b2b/my/stores").then((s) => setStore((s.data || []).find((x) => x.id === prod.store_id) || null)).catch(() => {});
    }).catch((e) => setErr(e?.response?.status === 403 ? "Brak dostępu do tego produktu." : "Produkt niedostępny."));
  }, [pid, auth?.ready, auth?.customer]);

  const disabled = useMemo(() => new Set(p?.disabled_variants || []), [p]);
  const sizeDisabled = (s) => disabled.has(`${color}|${s}`);
  const total = Object.values(qty).reduce((n, q) => n + (parseInt(q) || 0), 0);

  if (auth?.ready && !auth.customer) { navigate("/sklep/moj-sklep"); return null; }
  if (err) return <RedfoxLayout><div className="mx-auto max-w-md px-4 py-24 text-center text-neutral-400" data-testid="redfox-product-error">{err}</div></RedfoxLayout>;
  if (!p) return <RedfoxLayout><div className="py-24 text-center text-neutral-500">Ładowanie…</div></RedfoxLayout>;

  const colors = p.colors || [];
  const variants = p.variants || [];
  const needColor = colors.length > 0 && !color;
  const needVariant = variants.length > 0 && !variant;
  const canChooseSize = !needColor && !needVariant;

  const pickColor = (c) => {
    setColor(c); setQty({});
    const ph = (p.color_photos || {})[c];
    if (ph) setMainPhoto(ph);
  };
  const pickVariant = (v) => {
    setVariant(v);
    if (v?.photo) setMainPhoto(v.photo);
  };

  const add = () => {
    if (needColor) return toast.error("Najpierw wybierz kolor produktu");
    if (needVariant) return toast.error("Wybierz kolor nadruku / wariant");
    const clean = {};
    Object.entries(qty).forEach(([s, q]) => { const n = parseInt(q) || 0; if (n > 0 && !sizeDisabled(s)) clean[s] = n; });
    if (!Object.keys(clean).length) return toast.error("Wpisz ilość w co najmniej jednym rozmiarze");
    addSizes(p, color, variant, clean);
    toast.success("Dodano do koszyka");
    setQty({});
  };

  let step = 1;

  return (
    <RedfoxLayout store={store}>
      <div className="mx-auto max-w-6xl px-4 py-6">
        <Link to="/sklep/moj-sklep" className="inline-flex items-center gap-1.5 text-sm text-neutral-400 hover:text-white mb-6"><ArrowLeft className="h-4 w-4" />Powrót do kolekcji</Link>
        <div className="grid lg:grid-cols-2 gap-8">
          {/* Galeria */}
          <div>
            <div className="aspect-[4/5] overflow-hidden rounded-2xl bg-neutral-900 p-3" data-testid="redfox-gallery-main">
              {mainPhoto ? <img src={`${API}/files/${mainPhoto}`} alt={p.name} className="h-full w-full object-contain" /> : <div className="grid h-full place-items-center text-neutral-600"><PackageOpen className="h-12 w-12" /></div>}
            </div>
            {(p.photos || []).length > 1 && (
              <div className="mt-3 flex gap-2 flex-wrap">
                {p.photos.map((ph) => (
                  <button key={ph} onClick={() => setMainPhoto(ph)} className={`h-16 w-16 overflow-hidden rounded-lg ring-2 transition ${mainPhoto === ph ? "ring-red-600" : "ring-transparent"}`}>
                    <img src={`${API}/files/${ph}`} alt="" className="h-full w-full object-contain bg-neutral-900" />
                  </button>
                ))}
              </div>
            )}
          </div>

          {/* Info + wybór */}
          <div>
            <h1 className="text-3xl sm:text-4xl leading-tight uppercase italic" style={{ fontFamily: "'Oswald',sans-serif", fontWeight: 700 }}>{p.name}</h1>
            {p.subtitle && <p className="mt-1 text-neutral-400">{p.subtitle}</p>}
            <p className="mt-4 text-3xl" style={{ fontFamily: "'Oswald',sans-serif", fontWeight: 700 }}>{zl(p.price_grosz)}</p>
            {p.description && <p className="mt-4 text-sm text-neutral-300 whitespace-pre-line leading-relaxed">{p.description}</p>}

            {/* KROK 1 — kolor produktu */}
            {colors.length > 0 && (
              <div className="mt-6" data-testid="redfox-step-color">
                <StepHeader n={step++} label="Kolor produktu" done={!!color} />
                <div className="flex flex-wrap gap-2">
                  {colors.map((c) => (
                    <button key={c} data-testid={`redfox-color-${c}`} onClick={() => pickColor(c)}
                      className={`inline-flex items-center gap-2 rounded-full px-3 py-1.5 text-sm font-semibold transition ${color === c ? "bg-white text-neutral-950" : "bg-white/5 text-neutral-200 hover:bg-white/10"}`}>
                      {(p.color_photos || {})[c] && <img src={`${API}/files/${(p.color_photos || {})[c]}`} alt="" className="h-6 w-6 rounded-full object-cover bg-neutral-800" />}
                      {c}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* KROK 2 — kolor nadruku / wariant */}
            {variants.length > 0 && (
              <div className={`mt-6 transition ${needColor ? "pointer-events-none opacity-40" : ""}`} data-testid="redfox-step-variant">
                <StepHeader n={step++} label="Kolor nadruku / wariant" done={!!variant} />
                <div className="flex flex-wrap gap-2">
                  {variants.map((v) => (
                    <button key={v.id || v.name} data-testid={`redfox-variant-${v.id || v.name}`} onClick={() => pickVariant(v)}
                      className={`inline-flex items-center gap-2 rounded-xl border p-1.5 pr-3 text-sm font-semibold transition ${variant && (variant.id || variant.name) === (v.id || v.name) ? "border-red-600 bg-red-600/15 text-white" : "border-white/15 text-neutral-200 hover:border-red-600/50"}`}>
                      <span className="h-10 w-10 overflow-hidden rounded-lg bg-neutral-800">
                        {v.photo ? <img src={`${API}/files/${v.photo}`} alt="" className="h-full w-full object-contain" /> : <span className="grid h-full place-items-center text-neutral-600"><PackageOpen className="h-5 w-5" /></span>}
                      </span>
                      {v.name || v.print_color || "Wariant"}
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* KROK 3 — rozmiary i ilości */}
            <div className={`mt-6 transition ${canChooseSize ? "" : "pointer-events-none opacity-40"}`} data-testid="redfox-step-size">
              <StepHeader n={step++} label="Rozmiary i ilości" done={total > 0} />
              {!canChooseSize && <p className="mb-2 text-xs text-amber-300">{needColor ? "Najpierw wybierz kolor produktu." : "Wybierz kolor nadruku / wariant."}</p>}
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2" data-testid="redfox-size-grid">
                {(p.sizes || []).map((s) => {
                  const off = sizeDisabled(s);
                  return (
                    <div key={s} className={`rounded-xl border p-2 text-center ${off ? "border-white/5 opacity-40" : "border-white/15"}`}>
                      <div className="text-sm font-bold">{s}</div>
                      {off ? <div className="mt-1 text-[10px] text-neutral-500">niedostępny</div> : (
                        <input data-testid={`redfox-qty-${s}`} type="number" min="0" inputMode="numeric" value={qty[s] ?? ""} placeholder="0" disabled={!canChooseSize}
                          onChange={(e) => { const v = e.target.value; setQty((q) => ({ ...q, [s]: v === "" ? "" : Math.max(0, parseInt(v) || 0) })); }}
                          className="mt-1 w-full bg-transparent text-center text-lg font-extrabold outline-none" />
                      )}
                    </div>
                  );
                })}
              </div>
            </div>

            <Button data-testid="redfox-add-to-cart" onClick={add} disabled={total === 0 || !canChooseSize}
              className="mt-6 w-full h-12 bg-red-600 hover:bg-red-500 text-white font-bold uppercase tracking-wider gap-2 disabled:opacity-40">
              <Check className="h-5 w-5" />Dodaj do koszyka{total > 0 ? ` (${total} szt.)` : ""}
            </Button>

            {p.size_table && (
              <div className="mt-8">
                <p className="text-xs font-bold uppercase tracking-wider text-neutral-400 mb-2">Tabela rozmiarów</p>
                <pre data-testid="redfox-size-table" className="whitespace-pre-wrap rounded-xl bg-neutral-900 p-4 text-xs text-neutral-300 font-mono">{p.size_table}</pre>
              </div>
            )}
          </div>
        </div>
      </div>
    </RedfoxLayout>
  );
}
